import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { parseAndFormatPhone } from "../../utils/phoneUtils";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ open, onClose }) => {
  const { user, refreshProfile } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      let formattedPhone = null;
      if (phone) {
        formattedPhone = parseAndFormatPhone(phone);
      }

      const updates: Record<string, string | undefined> = {
        full_name: fullName,
        display_name: fullName.split(" ")[0], // Default display name
        updated_at: new Date().toISOString(),
      };

      if (formattedPhone) {
        updates.phone = formattedPhone;
      }

      const { error } = await supabase.from("profiles").update(updates).eq("id", user.id);

      if (error) throw error;

      await refreshProfile();
      enqueueSnackbar("Profile updated successfully!", { variant: "success" });
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update profile";
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Welcome to Obsidian Ridge AI</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Please take a moment to complete your profile. This helps us personalize your
              experience.
            </Typography>
            <Stack spacing={3}>
              <TextField
                autoFocus
                label="Full Name"
                fullWidth
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="e.g. Jane Doe"
              />
              <TextField
                label="Phone Number (Optional)"
                fullWidth
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                helperText="We'll use this for important account notifications."
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleSkip} color="inherit">
            Skip for Now
          </Button>
          <Button type="submit" variant="contained" disabled={loading || !fullName.trim()}>
            {loading ? "Saving..." : "Complete Profile"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
